import type MachineBase from './machines/MachineBase';

export default class StateMachines<T> {
  // #region MAIN
  list: Record<string, MachineBase> = {}; // Collection of registered state machines
  stack: Array<MachineBase> = []; // Stack of active state machines
  obj!: T;

  constructor(obj: T) {
    this.obj = obj;
  }
  // #endregion

  // #region SETTERS / GETTERS

  // Register a state machine
  reg(...ary: Array<MachineBase>): this {
    for (const sm of ary) this.list[sm.name] = sm;
    return this;
  }

  // Get the top most active state machine in the stack
  getCurrent(): MachineBase | undefined {
    return this.stack.at(-1);
  }

  // Check if the named state machine is currently in the stack
  isActive(name: string): boolean {
    for (const m of this.stack) {
      if (m.name === name) return true;
    }
    return false;
  }

  get stackSize(): number {
    return this.stack.length;
  }

  getMachine(name: string): MachineBase | null {
    const sm = this.list[name];
    return sm ? sm : null;
  }

  // #endregion

  // #region MANAGE STACK

  // Push a new machine to the top of the stack
  push(name: string): this {
    const sm = this.list[name];
    if (sm) {
      if (!sm.validateStartup(this.obj)) {
        console.log('Machine failed startup validation', sm.name);
        return this;
      }

      if (this.stack.length > 0) {
        const prev = this.getCurrent();
        if (prev) prev.onSuspend(this.obj); // Pause active machine
      }

      sm.onInit(this.obj); // Initialize new machine
      this.stack.push(sm); // New machine is now the active one
    } else {
      console.error('State machine not found: ', name);
    }

    return this;
  }

  // Remove top active machine & reactivate previous one
  pop() {
    const idx = this.stack.length - 1;
    if (idx !== 0) {
      let sm = this.stack.pop();
      if (sm) sm.onRelease(this.obj); // End existing machine

      sm = this.stack.at(-1);
      if (sm) sm.onWakeup(this.obj); // Reactivate previous machine
    }
    return this;
  }

  // Swop top active machine with a new one
  switch(name: string) {
    const sm = this.list[name];
    if (sm) {
      if (!sm.validateStartup(this.obj)) {
        console.log('Machine failed startup validation', sm.name);
        return this;
      }

      const idx = this.stack.length - 1;
      this.stack[idx].onRelease(this.obj); // End existing machine
      sm.onInit(this.obj); // Start new machine
      this.stack[idx] = sm; // Make it the most active
    } else {
      console.error('State machine not found: ', name);
    }

    return this;
  }

  // Exit all machines
  clear() {
    if (this.stack.length > 0) {
      while (this.stack.length > 0) {
        this.stack.pop()?.onRelease(this.obj);
      }
    }
    return this;
  }

  // #endregion
}
